/**
 * The filler between a Venus wood hood and the uppers beside it. A wood
 * hood's moldings stand proud of its sides (`woodHoods.woodHoodSideProudM`),
 * so the uppers stand clear of them (`applianceInstall.hoodSkySpans` reserves
 * the hood's box and its moldings) and a filler closes the gap from the
 * hood's side out to the upper, the moldings returning onto it — cut flush
 * at the hood they "dive into the front of the upper cabinet doors" (the
 * user, 2026-11-01).
 *
 * One filler a side: the leftover the packer put against the hood (a
 * remainder filler, or the bare slot too narrow for one) is part of it, as a
 * corner's reveal takes the leftover beside it (`cornerReveal.js`). A gap a
 * cabinet would have fitted — the packer left it for a window — is left as
 * it is.
 *
 * Pure: 1-D params in, boxes out.
 */
const EPS = 1e-6;

const isRemainderFiller = (b) => b.filler === true && !b.override;

/**
 * The planned box nearest the hood on one side — the one whose end is
 * closest to `edge` without crossing it — or null.
 */
function nearestBeside(boxes, edge, side) {
  let best = null;
  for (const b of boxes) {
    if (side === 'lo' ? b.end > edge + EPS : b.start < edge - EPS) continue;
    if (!best || (side === 'lo' ? b.end > best.end : b.start < best.start))
      best = b;
  }
  return best;
}

/**
 * A segment's planned uppers with a filler each side of each wood hood on it,
 * from the hood's box out to the box nearest it.
 *
 * @param {{start, end, filler?, override?}[]} boxes the segment's planned
 *   upper boxes (params)
 * @param {{lo, hi, box: {lo, hi}}[]} hoods the wood hoods' reserved spans on
 *   this segment, their boxes within
 * @param {(width: number) => boolean} cabinetFits whether the run's line
 *   makes a cabinet no wider than `width`
 * @returns {Object[]} the boxes, a filler beside each hood where one belongs
 */
function withHoodClearance(boxes, hoods, cabinetFits) {
  let out = [...boxes];
  for (const hood of hoods) {
    for (const side of ['lo', 'hi']) {
      const reserved = side === 'lo' ? hood.lo : hood.hi;
      const face = side === 'lo' ? hood.box.lo : hood.box.hi;
      const near = nearestBeside(out, reserved, side);
      if (!near) continue;
      // what stands between the reserved span's edge and that box
      const slot = side === 'lo' ? reserved - near.end : near.start - reserved;
      if (slot > EPS && cabinetFits(slot)) continue;
      // a remainder filler against the hood becomes part of the one filler
      const joins = isRemainderFiller(near);
      const from = joins ? (side === 'lo' ? near.start : near.end) : null;
      if (joins) out = out.filter((b) => b !== near);
      const span =
        side === 'lo'
          ? { start: joins ? from : near.end, end: face }
          : { start: face, end: joins ? from : near.start };
      if (span.end - span.start > EPS)
        out.push({ ...span, filler: true, hoodClearance: true });
    }
  }
  return out.sort((a, b) => a.start - b.start);
}

module.exports = { withHoodClearance };
